/*
 * "Follow up · since last visit" model (CONTRACT §3.3, §4). Pure: no React,
 * no network. The server computes the rows and their order (critical first);
 * the UI filters and never re-sorts.
 */
import { shortDate } from "./recsModel";

export const fuRows = (body) => (body && body.followup && Array.isArray(body.followup.rows) ? body.followup.rows : []);

// Reviewable rows the UI shows: reviewed at an earlier visit with unchanged
// evidence are hidden (D8).
export const visibleRows = (body) =>
  fuRows(body).filter((r) => r.display === "row" && !(r.status === "reviewed" && r.reviewed && !r.reviewed.this_visit));
export const openFuRows = (body) => fuRows(body).filter((r) => r.display === "row" && r.status === "open");
export const reviewedFuThisVisit = (body) =>
  fuRows(body)
    .filter((r) => r.display === "row" && r.status === "reviewed" && r.reviewed && r.reviewed.this_visit)
    .sort((a, b) => String(a.reviewed.at).localeCompare(String(b.reviewed.at)));
export const lineRows = (body) => fuRows(body).filter((r) => r.display === "line");
export const foldedRows = (body) => fuRows(body).filter((r) => r.display === "folded");

// "Follow up · since Sep 11" / "Follow up · last 90 days"
export const fuTitle = (body) => {
  const fu = body && body.followup;
  const dosYear = String((body && body.meta && body.meta.visit && body.meta.visit.dos) || "").slice(0, 4);
  if (fu && fu.last_visit && fu.last_visit.date) return `Follow up · since ${shortDate(fu.last_visit.date, dosYear)}`;
  if (fu && fu.window && fu.window.basis === "no_last_visit_90d") return "Follow up · last 90 days";
  return "Follow up";
};

// "Sep 11 plan" before the first plan row, when there is a last visit.
export const planHeading = (body) => {
  const fu = body && body.followup;
  if (!fu || !fu.last_visit || !fu.last_visit.date) return null;
  const dosYear = String((body.meta && body.meta.visit && body.meta.visit.dos) || "").slice(0, 4);
  return `${shortDate(fu.last_visit.date, dosYear)} plan`;
};

/*
 * Follow up's mode (careiq-final):
 *   line  nothing open (and not asked to show): one grey "all N reviewed today · Show" line
 *   lead  the first open row + "N more": while recording or after, and on stacked screens
 *   full  every row (before recording on desktop / iPad landscape, or after "Show")
 */
export const fuMode = ({ openCount, fuShow = false, fuOpen = false, before = true, stacked = false }) => {
  if (!openCount && !fuShow) return "line";
  if (openCount && !fuOpen && (!before || stacked)) return "lead";
  return "full";
};

/*
 * The timeline's Follow up head (owner 10-06: the patient's own timeline must
 * stay in view below it). Before recording on desktop / iPad landscape, and in
 * the rest of the stacked split, it shows every critical row (else the first
 * row) plus at most FU_HEAD_MORE more items, then "N more · Show". Items are
 * counted in display order: rows, the last visit's plan rows, its plan lines.
 * The folded normal results wait until nothing is held back.
 */
export const FU_HEAD_MORE = 2;
export const fuHeadCap = (open) => Math.max(1, (open || []).filter((r) => r.group === "critical").length) + FU_HEAD_MORE;

// {nonPlan, planRows, lines, folded, hidden, capped, total} of a full block
// (part undefined) or of the rest of a stacked split (part "rest": its lead
// sits above the drawer and counts against the cap).
export const fuHeadView = (body, { part, expanded = false } = {}) => {
  const open = openFuRows(body);
  const rows = part === "rest" ? open.slice(1) : open;
  const budget = fuHeadCap(open) - (part === "rest" ? 1 : 0);
  const nonPlan = rows.filter((r) => r.group !== "plan");
  const planRows = rows.filter((r) => r.group === "plan");
  const lines = lineRows(body);
  const folded = foldedRows(body);
  const total = nonPlan.length + planRows.length + lines.length;
  if (expanded || total <= budget) return { nonPlan, planRows, lines, folded, hidden: 0, capped: false, total, budget };
  let left = budget;
  const take = (list) => {
    const out = list.slice(0, Math.max(0, left));
    left -= out.length;
    return out;
  };
  const shown = { nonPlan: take(nonPlan), planRows: take(planRows), lines: take(lines) };
  const n = shown.nonPlan.length + shown.planRows.length + shown.lines.length;
  return { ...shown, folded: [], hidden: total - n, capped: true, total, budget };
};

// The rest of a stacked split has nothing under its lead (no row, line,
// folded result or reviewed-today list): it is not rendered at all.
export const fuRestEmpty = (body, stateOk) => {
  const v = fuHeadView(body, { part: "rest" });
  return !v.total && !v.folded.length && !(stateOk && reviewedFuThisVisit(body).length);
};

const capFirst = (s) => (/^[a-z]/.test(s) ? s[0].toUpperCase() + s.slice(1) : s);

/*
 * Readability of a Follow up row (lead decision D3, 10-06; owner: "more
 * visual clarity… colour critical values, bold, etc… more readable than a
 * blob of text in the same colour"). The server's row, read into parts the
 * row renders separately:
 *   kind   lab | imaging | doc | hospital | med | plan | order (a small muted icon)
 *   name   the item, semibold
 *   value  the key value / result, tabular, coloured by sev; dir adds ↑ / ↓,
 *          flag "abnormal" the word when there is no direction
 *   sev    critical (red family + a "Critical" chip) | abnormal (amber) | null (ink)
 *   values an abnormal panel's analytes [{name, value, dir}] (amber, ↑ / ↓)
 *   meta   grey, one line: date · panel / count · "summary pending" · source
 *   detail content lines (a plan's text, a critical row's other results)
 *   ai     the AI summary, at most one clause-clipped line
 * Colour is never alone: the arrow, the chip and the words carry it too.
 * A row in a shape this does not know keeps its text and lines.
 */
export const AI_LINE_MAX = 46;
const BOUNDARY = /[;,:]\s|\.\s|\s[—–-]\s/g;

const MIN_CLAUSE = 12;

// One line of an AI summary, cut at the last clause boundary (; , : . —) that
// fits, else (no clause of MIN_CLAUSE+ characters fits) at a word, with "…".
// A sentence that fits stays whole.
export const clipClause = (text, max = AI_LINE_MAX) => {
  const t = String(text || "").replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  let cut = -1;
  let m;
  BOUNDARY.lastIndex = 0;
  while ((m = BOUNDARY.exec(t)) && m.index <= max) cut = m.index;
  if (cut >= MIN_CLAUSE) return `${t.slice(0, cut).replace(/[\s,;:.—–-]+$/, "")}…`;
  const sp = t.lastIndexOf(" ", max);
  return `${t.slice(0, sp > max * 0.4 ? sp : max).replace(/[\s,;:.—–-]+$/, "")}…`;
};

const WHEN = "([A-Z][a-z]{2} \\d{1,2}(?:, \\d{4})?)";
const LAB_SUB = new RegExp(`^(.+?) · (.+), ${WHEN}$`);
const dirOf = (s) => (/\bhigh\b/i.test(s || "") ? "high" : /\blow\b/i.test(s || "") ? "low" : null);
const NUMERIC = /^[<>≤≥]?=?-?\d/;
// a value that is a number ("18", "<0.5", ">=60", "1:80"), not a word that starts with one ("25-Hydroxy", "10^3/uL")
const NUMBER = /^[<>≤≥]?=?[-+]?\d+(?:[.,]\d+)?(?:[eE][-+]?\d+)?(?::\d+)?$/;

// "Creatinine 1.6 mg/dL high" -> {name: "Creatinine", value: "1.6 mg/dL", dir: "high"}
// The value starts at the first plain number after the name ("Vitamin D, 25-Hydroxy 18 ng/mL low"
// -> "Vitamin D, 25-Hydroxy" / "18 ng/mL"), else at the first word that starts with a digit ("0-2 /HPF").
export const analyteBit = (bit) => {
  const raw = String(bit || "").trim();
  const m = /^(.*?)\s+(high|low|abnormal)$/i.exec(raw);
  const body = m ? m[1] : raw;
  const flag = m ? m[2].toLowerCase() : null;
  const words = body.split(" ");
  const num = words.findIndex((w, i) => i > 0 && NUMBER.test(w));
  const at = num > 0 ? num : words.findIndex((w, i) => i > 0 && NUMERIC.test(w));
  if (at < 0) {
    // no number: "Urine protein Positive abnormal" -> the last word is the value
    if (words.length < 2) return { name: body, value: null, dir: flag === "abnormal" ? null : flag, flag };
    return { name: words.slice(0, -1).join(" "), value: words[words.length - 1], dir: flag === "abnormal" ? null : flag, flag };
  }
  return { name: words.slice(0, at).join(" "), value: words.slice(at).join(" "), dir: flag === "abnormal" ? null : flag, flag };
};

const kindOf = (row) => {
  if (row.kind === "fu_lab") return "lab";
  if (row.kind === "fu_med") return "med";
  if (row.kind === "fu_plan") return "plan";
  if (row.kind === "fu_order") return "order";
  if (row.kind === "fu_doc") {
    if (row.group === "hospital") return "hospital";
    return row.doc && row.doc.category === "imagingresult" ? "imaging" : "doc";
  }
  return null;
};

export const fuRowView = (row, refYear) => {
  const kind = kindOf(row);
  const sub = Array.isArray(row.sub) ? row.sub.map((l) => String(l || "").trim()).filter(Boolean) : [];
  const v = { kind, name: capFirst(String(row.text || "")), value: row.value || null, dir: null, flag: null, sev: null, chip: null, values: [], meta: [], detail: [], ai: null, count: null };
  if (row.ai_quote) v.ai = clipClause(row.ai_quote);
  if (kind === "imaging" || kind === "doc" || kind === "hospital") {
    const when = shortDate(row.date, refYear);
    const text = String(row.text || "");
    const filed = /^Filed\s+(.+)$/.exec(sub[0] || "");
    if (filed && when && filed[1].startsWith(when) && text.endsWith(`, ${when}`) && text.length > when.length + 2) {
      let name = text.slice(0, -(when.length + 2));
      const n = /^(.*\S)\s+×(\d+)$/.exec(name);
      if (n) {
        name = n[1];
        v.count = Number(n[2]);
      }
      v.name = capFirst(name);
      v.meta = [filed[1], ...(v.count ? [`${v.count} files`] : []), ...sub.slice(1).map((l) => (/^AI summary pending$/i.test(l) ? "summary pending" : l))];
    } else {
      v.meta = sub;
    }
  } else if (kind === "lab") {
    const crit = row.group === "critical";
    const m = LAB_SUB.exec(sub[0] || "");
    if (row.value) {
      // one analyte: critical (red + chip) or a not-critical band (amber)
      v.sev = crit ? "critical" : "abnormal";
      v.chip = crit ? "Critical" : null;
      v.dir = (crit && row.critical && row.critical.direction) || dirOf(m ? m[1] : sub[0]);
      // no direction (a band row flagged "critical" alone): the word, never the colour alone
      v.flag = !crit && !v.dir ? "abnormal" : null;
      v.meta = m ? [m[3], m[2]] : sub.slice(0, 1);
      v.detail = sub.slice(1);
    } else {
      // an abnormal panel: "BMP, Sep 24" over "2 abnormal · Creatinine 1.6 mg/dL high · …"
      const when = shortDate(row.date, refYear);
      const text = String(row.text || "");
      const parts = String(sub[0] || "").split(" · ");
      const head = /^\d+ abnormal$/.exec(parts[0] || "");
      if (head && when && text.endsWith(`, ${when}`)) {
        v.name = capFirst(text.slice(0, -(when.length + 2)));
        v.values = parts.slice(1).map(analyteBit);
        v.sev = v.values.length ? "abnormal" : null;
        v.meta = [when, parts[0]];
        v.detail = sub.slice(1);
        // a one-test panel named after its test ("Hemoglobin A1c, Sep 30"): the value sits on the title line
        const only = v.values.length === 1 ? v.values[0] : null;
        if (only && only.value && only.name.toLowerCase() === v.name.toLowerCase()) {
          v.value = only.value;
          v.dir = only.dir;
          v.flag = only.dir ? null : "abnormal";
          v.values = [];
          v.meta = [when];
        }
      } else {
        v.meta = sub;
      }
    }
    if (row.source) v.meta.push(row.source);
  } else if (kind === "med") {
    const m = /^(.*\S)\s+(held|stopped|new Rx|started|restarted|changed)$/i.exec(String(row.text || ""));
    if (m) {
      v.name = m[1];
      v.value = m[2];
      v.sev = "abnormal";
    }
    v.meta = sub;
  } else if (kind === "plan") {
    v.detail = sub;
  } else {
    v.meta = sub;
  }
  if (row.reopened) v.meta.push(row.reopen_reason === "order_removed" ? "reopened: order removed" : "reopened: new evidence");
  return v;
};

// Stacked screens before recording: the critical lead sits above the drawer
// and the rest of Follow up heads the timeline (nothing hidden behind a tap).
export const fuSplit = ({ stacked, before, mode }) => Boolean(stacked && before && mode === "lead");

/*
 * A plan line ("○ Surgical referral for adrenal mass — not on record · in AI
 * Recommendations"). The pointed recommendation's live state wins over the
 * server's line_mark / sub.
 */
export const planLineView = (line, { recsByKey = new Map(), rowsByKey = new Map() } = {}) => {
  const rec = line.pointer_issue_key ? recsByKey.get(line.pointer_issue_key) : null;
  if (rec) {
    if (rec.status === "open") return { mark: "○", text: `${line.text} — not on record · `, link: { issue_key: rec.issue_key, label: "in AI Recommendations" } };
    if (rec.reviewed && rec.reviewed.via === "order") return { mark: "✓", text: `${line.text} — added today · in AI Recommendations`, link: null };
    return { mark: "○", text: `${line.text} — not on record · reviewed in AI Recommendations`, link: null };
  }
  const mark = line.line_mark === "done" ? "✓" : "○";
  let text = line.text;
  if (line.sub && line.sub[0]) text += ` — ${line.sub[0]}`;
  const target = line.pointer_row_key ? rowsByKey.get(line.pointer_row_key) : null;
  if (target) {
    const name = String(target.text || "").split(",")[0].trim();
    const inLine = name && line.text.toLowerCase().includes(name.toLowerCase());
    text += inLine || !name ? " (above)" : ` (${name.toLowerCase()} above)`;
  }
  return { mark, text, link: null };
};

// Undo with no target: the newest reviewed.at in this scope.
export const newestReviewed = (rows) =>
  (rows || []).reduce((a, b) => (!a || String(b.reviewed && b.reviewed.at) > String(a.reviewed && a.reviewed.at) ? b : a), null);
